import React, { useState, useRef } from 'react';
import { InspectionItem } from '../../types';
import { X, Camera, Trash2, Save, Upload } from 'lucide-react';

interface ChecklistEditorProps {
  item?: InspectionItem;
  onSave: (item: InspectionItem) => void;
  onClose: () => void;
}

const ChecklistEditor: React.FC<ChecklistEditorProps> = ({ item, onSave, onClose }) => {
  const [description, setDescription] = useState(item?.description || '');
  const [category, setCategory] = useState(item?.category || 'General');
  const [photos, setPhotos] = useState<string[]>(item?.photos || []);
  const [isPassed, setIsPassed] = useState(item?.isPassed || false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      files.forEach((file: File) => {
        if (!file.type.startsWith('image/')) return;
        
        const reader = new FileReader();
        reader.onloadend = () => {
          if (reader.result) {
            setPhotos(prev => [...prev, reader.result as string]);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removePhoto = (index: number) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    if (!description.trim()) return;
    
    const newItem: InspectionItem = {
      id: item?.id || Date.now().toString(),
      description,
      category,
      isPassed,
      photos,
      notes: item?.notes || ''
    };
    onSave(newItem);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" onClick={onClose}></div>
      <div className="bg-slate-900 rounded-2xl shadow-2xl border border-slate-700 w-full max-w-lg relative flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-200">
        <div className="px-6 py-4 border-b border-slate-800 flex justify-between items-center">
          <h3 className="font-bold text-lg text-white">
            {item ? 'Edit Inspection Item' : 'New Inspection Item'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto custom-scrollbar">
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Description</label>
              <input 
                value={description}
                onChange={e => setDescription(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3 text-white outline-none focus:border-emerald-500 transition-colors"
                placeholder="e.g., Kitchen sink installation check"
              />
            </div>
            
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Category</label>
              <select 
                value={category}
                onChange={e => setCategory(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3 text-white outline-none focus:border-emerald-500 transition-colors"
              >
                <option value="General">General</option>
                <option value="Plumbing">Plumbing</option>
                <option value="Electrical">Electrical</option>
                <option value="HVAC">HVAC</option>
                <option value="Finishing">Finishing</option>
                <option value="Structural">Structural</option>
              </select>
            </div>

            <div className="flex items-center gap-3 py-2">
               <button 
                 onClick={() => setIsPassed(!isPassed)}
                 className={`w-12 h-6 rounded-full p-1 transition-colors ${isPassed ? 'bg-emerald-500' : 'bg-slate-700'}`}
               >
                 <div className={`w-4 h-4 bg-white rounded-full transform transition-transform ${isPassed ? 'translate-x-6' : 'translate-x-0'}`} />
               </button>
               <span className={`text-sm font-bold ${isPassed ? 'text-emerald-400' : 'text-slate-400'}`}>
                 {isPassed ? 'Passed Inspection' : 'Pending / Failed'}
               </span>
            </div>
          </div>

          {/* Photos Section */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="text-xs font-bold text-slate-500 uppercase">Evidence Photos</label>
              <button 
                onClick={() => fileInputRef.current?.click()}
                className="text-xs bg-slate-800 hover:bg-blue-600 text-white px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors"
              >
                <Camera className="w-3 h-3" /> Add Photo
              </button>
              <input 
                type="file" 
                ref={fileInputRef} 
                className="hidden" 
                accept="image/*" 
                multiple 
                onChange={handlePhotoUpload} 
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              {photos.map((photo, idx) => (
                <div key={idx} className="relative aspect-square rounded-xl overflow-hidden border border-slate-700 group">
                  <img src={photo} alt="Inspection" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button onClick={() => removePhoto(idx)} className="text-red-400 hover:text-red-300 p-2">
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
              {photos.length === 0 && (
                <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="col-span-3 border-2 border-dashed border-slate-800 rounded-xl h-24 flex flex-col items-center justify-center text-slate-500 hover:border-slate-600 hover:bg-slate-900/50 cursor-pointer transition-all"
                >
                    <Upload className="w-6 h-6 mb-1 opacity-50" />
                    <span className="text-xs">Upload Images</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-800 bg-slate-900 rounded-b-2xl flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-slate-400 hover:text-white font-medium transition-colors">Cancel</button>
          <button 
            onClick={handleSave}
            className="bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-2 rounded-xl font-bold shadow-lg shadow-emerald-900/20 transition-all flex items-center gap-2"
          >
            <Save className="w-4 h-4" /> Save Item
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChecklistEditor;